import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Select, message } from '@surecompanies/core_components'
import { Tooltip } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { type PlanReportType } from '../constants'
import {
  exportCountyMismatchReport,
  exportMissingBenefitReport,
  exportMissingRatingAreaPriceReport,
  exportMissingSbcReport,
  exportPlanChangeImpacts,
  exportPlanData,
  exportServiceAreaMismatchReport,
  getDbAvailablePlanYears,
  parseBlobError,
} from '../api/plan-management-v3-api'
import DownloadPlanDataCsvModal, {
  type PlanDataCsvDownloadParams,
} from './DownloadPlanDataCsvModal'
import ExportPlanReportsModal from './ExportPlanReportsModal'
import HealthPlanStatCards from './HealthPlanStatCards'
import HealthPlanUploadLogTable from './HealthPlanUploadLogTable'
import HealthPlanValidationBanner from './HealthPlanValidationBanner'
import UploadHealthPlanCsvModal from './UploadHealthPlanCsvModal'
import styles from '../PlanManagementV3.module.scss'

const REPORT_EXPORTERS: Record<
  PlanReportType,
  (year: number, onProgress?: (bytesLoaded: number) => void) => Promise<void>
> = {
  PLAN_CHANGE_IMPACT: exportPlanChangeImpacts,
  SERVICE_AREA_MISMATCH: exportServiceAreaMismatchReport,
  MISSING_RATING_AREA_PRICE: exportMissingRatingAreaPriceReport,
  MISSING_BENEFIT: exportMissingBenefitReport,
  MISSING_SBC: exportMissingSbcReport,
  COUNTY_MISMATCH: exportCountyMismatchReport,
}

export default function HealthPlanTab() {
  const queryClient = useQueryClient()
  const { flags } = useFeatureFlags()
  const isUploadEnabled =
    flags['admin.plan-management-v3-health-plan-upload-enabled']
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadedBytes, setDownloadedBytes] = useState(0)
  const [isExportingReport, setIsExportingReport] = useState(false)
  const [reportDownloadedBytes, setReportDownloadedBytes] = useState(0)
  const [year, setYear] = useState<number | undefined>(undefined)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const { data: years, isLoading: isLoadingYears } = useQuery({
    queryKey: ['dbAvailablePlanYears'],
    queryFn: getDbAvailablePlanYears,
  })

  const yearOptions = (years ?? []).map((y) => ({
    label: String(y),
    value: y,
  }))

  const hasAutoSelectedYearRef = useRef(false)
  useEffect(() => {
    if (yearOptions.length > 0 && !hasAutoSelectedYearRef.current) {
      setYear(yearOptions[0].value)
      hasAutoSelectedYearRef.current = true
    }
  }, [yearOptions])

  const handleDownload = useCallback(
    async (params: PlanDataCsvDownloadParams) => {
      if (!isMountedRef.current || isDownloading) return
      setIsDownloading(true)
      setDownloadedBytes(0)
      try {
        await exportPlanData(params, (bytesLoaded) => {
          if (!isMountedRef.current) return
          setDownloadedBytes(bytesLoaded)
        })
        if (!isMountedRef.current) return
        setIsDownloading(false)
        setIsModalOpen(false)
      } catch (err) {
        console.error('Failed to download production csv', err)
        const errorMsg = await parseBlobError(err)
        if (!isMountedRef.current) return
        message.error(errorMsg ?? 'An error occurred. Please try again.')
        setIsDownloading(false)
      }
    },
    [isDownloading]
  )

  const handleExportReport = useCallback(
    async (reportType: PlanReportType) => {
      if (!isMountedRef.current || isExportingReport || year === undefined)
        return
      setIsExportingReport(true)
      setReportDownloadedBytes(0)
      try {
        await REPORT_EXPORTERS[reportType](year, (bytesLoaded) => {
          if (!isMountedRef.current) return
          setReportDownloadedBytes(bytesLoaded)
        })
        if (!isMountedRef.current) return
        setIsExportingReport(false)
        setIsReportModalOpen(false)
      } catch (err) {
        console.error('Failed to download plan report csv', err)
        const errorMsg = await parseBlobError(err)
        if (!isMountedRef.current) return
        message.error(errorMsg ?? 'An error occurred. Please try again.')
        setIsExportingReport(false)
      }
    },
    [isExportingReport, year]
  )

  const handleUploaded = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['healthPlanUploadLogs'] })
    void queryClient.invalidateQueries({ queryKey: ['healthPlanUploaders'] })
    void queryClient.invalidateQueries({
      queryKey: ['healthPlanValidationSummary'],
    })
  }, [queryClient])

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h1 className={styles.cardHeaderTitle}>Health Plan</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            style={{ width: 90 }}
            placeholder="Year"
            loading={isLoadingYears}
            value={year}
            onChange={(v) => setYear(v)}
            options={yearOptions}
          />
          <Button
            disabled={year === undefined}
            onClick={() => setIsReportModalOpen(true)}
          >
            Export Plan Reports
          </Button>
          <Button disabled={isDownloading} onClick={() => setIsModalOpen(true)}>
            Download from DB
          </Button>
          <Tooltip
            title={isUploadEnabled ? undefined : 'Upload is currently disabled'}
          >
            {/* antd Tooltip doesn't fire on a disabled Button's own pointer
                events — the wrapping span keeps hover working either way. */}
            <span>
              <Button
                type="primary"
                disabled={!isUploadEnabled}
                onClick={() => setIsUploadModalOpen(true)}
              >
                Upload
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <HealthPlanValidationBanner year={year} />

      <div style={{ marginBottom: 24 }}>
        <HealthPlanStatCards year={year} />
      </div>

      <HealthPlanUploadLogTable />

      <DownloadPlanDataCsvModal
        open={isModalOpen}
        year={year}
        isDownloading={isDownloading}
        downloadedBytes={downloadedBytes}
        onClose={() => setIsModalOpen(false)}
        onDownload={handleDownload}
      />

      <ExportPlanReportsModal
        open={isReportModalOpen}
        year={year}
        isExporting={isExportingReport}
        downloadedBytes={reportDownloadedBytes}
        onClose={() => setIsReportModalOpen(false)}
        onExport={handleExportReport}
      />

      <UploadHealthPlanCsvModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploaded={handleUploaded}
      />
    </div>
  )
}
